'use strict';

/**
 * Modern Client-Side Contact Form Validation & AJAX Handler
 * Designed for Isaac Piñeiro Studio with custom inline error feedback.
 */
export const initContactForm = () => {
   const form = document.querySelector('.js-contact-form');
   if (!form) return;

   const nameInput = form.querySelector('.js-contact-name');
   const emailInput = form.querySelector('.js-contact-email');
   const phoneInput = form.querySelector('.js-contact-phone');
   const messageInput = form.querySelector('.js-contact-message');
   const privacyCheckbox = form.querySelector('.js-contact-privacy');
   const submitBtn = form.querySelector('.js-contact-submit');
   const statusBox = form.querySelector('.js-contact-status');
   const honeypot = form.querySelector('#webcooking');

   // Language detection
   const langInput = form.querySelector('input[name="webcooking__contact_language"]');
   const isEn = langInput && langInput.value.toLowerCase().startsWith('en');

   const messages = {
      nameRequired: isEn ? 'Please enter your name.' : 'Por favor, introduce tu nombre.',
      nameTooShort: isEn ? 'Name is too short.' : 'El nombre debe tener al menos 2 caracteres.',
      emailRequired: isEn ? 'Please enter your email.' : 'Por favor, introduce tu correo electrónico.',
      emailInvalid: isEn ? 'Please enter a valid email address.' : 'Introduce una dirección de correo válida.',
      phoneInvalid: isEn ? 'Please enter a valid phone number.' : 'Introduce un número de teléfono válido.',
      messageRequired: isEn ? 'Please enter your message.' : 'Por favor, escribe tu mensaje.',
      messageTooShort: isEn ? 'Message is too short.' : 'El mensaje debe tener al menos 5 caracteres.',
      privacyRequired: isEn ? 'You must accept the privacy policy.' : 'Debes aceptar la política de privacidad.',
      sending: isEn ? 'Sending...' : 'Enviando...',
      sendSuccess: isEn ? 'Thank you! Your message has been sent successfully. We will get back to you soon.' : '¡Gracias! Tu mensaje ha sido enviado correctamente. Nos pondremos en contacto contigo lo antes posible.',
      sendError: isEn ? 'There was an error sending your message. Please try again or email us directly.' : 'Ha ocurrido un error al enviar el formulario. Por favor, inténtalo de nuevo o escríbenos a info@isaacpineiro.com.',
   };

   // Helper: Set error on a field group
   const setError = (inputElement, errorMessage) => {
      const fieldGroup = inputElement.closest('.js-field-group');
      if (!fieldGroup) return;

      fieldGroup.classList.add('has-error');
      const errorMsgEl = fieldGroup.querySelector('.js-error-msg');
      if (errorMsgEl) {
         errorMsgEl.textContent = errorMessage;
      }
      inputElement.setAttribute('aria-invalid', 'true');
   };

   // Helper: Clear error on a field group
   const clearError = (inputElement) => {
      const fieldGroup = inputElement.closest('.js-field-group');
      if (!fieldGroup) return;

      fieldGroup.classList.remove('has-error');
      const errorMsgEl = fieldGroup.querySelector('.js-error-msg');
      if (errorMsgEl) {
         errorMsgEl.textContent = '';
      }
      inputElement.removeAttribute('aria-invalid');
   };

   // Validation rule: Name
   const validateName = () => {
      if (!nameInput) return true;
      const val = nameInput.value.trim();
      if (!val) {
         setError(nameInput, messages.nameRequired);
         return false;
      }
      if (val.length < 2) {
         setError(nameInput, messages.nameTooShort);
         return false;
      }
      clearError(nameInput);
      return true;
   };

   // Validation rule: Email
   const validateEmail = () => {
      if (!emailInput) return true;
      const val = emailInput.value.trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!val) {
         setError(emailInput, messages.emailRequired);
         return false;
      }
      if (!emailRegex.test(val)) {
         setError(emailInput, messages.emailInvalid);
         return false;
      }
      clearError(emailInput);
      return true;
   };

   // Validation rule: Phone (optional, but validated if filled)
   const validatePhone = () => {
      if (!phoneInput) return true;
      const val = phoneInput.value.trim();
      if (!val) {
         clearError(phoneInput);
         return true;
      }
      const phoneRegex = /^[\d\s\+\-\(\)\.]{6,25}$/;
      if (!phoneRegex.test(val)) {
         setError(phoneInput, messages.phoneInvalid);
         return false;
      }
      clearError(phoneInput);
      return true;
   };

   // Validation rule: Message
   const validateMessage = () => {
      if (!messageInput) return true;
      const val = messageInput.value.trim();
      if (!val) {
         setError(messageInput, messages.messageRequired);
         return false;
      }
      if (val.length < 5) {
         setError(messageInput, messages.messageTooShort);
         return false;
      }
      clearError(messageInput);
      return true;
   };

   // Validation rule: Privacy Checkbox
   const validatePrivacy = () => {
      if (!privacyCheckbox) return true;
      if (!privacyCheckbox.checked) {
         setError(privacyCheckbox, messages.privacyRequired);
         return false;
      }
      clearError(privacyCheckbox);
      return true;
   };

   // Real-time blur listeners
   if (nameInput) {
      nameInput.addEventListener('blur', validateName);
      nameInput.addEventListener('input', () => {
         if (nameInput.closest('.js-field-group')?.classList.contains('has-error')) {
            validateName();
         }
      });
   }

   if (emailInput) {
      emailInput.addEventListener('blur', validateEmail);
      emailInput.addEventListener('input', () => {
         if (emailInput.closest('.js-field-group')?.classList.contains('has-error')) {
            validateEmail();
         }
      });
   }

   if (phoneInput) {
      phoneInput.addEventListener('blur', validatePhone);
      phoneInput.addEventListener('input', () => {
         if (phoneInput.closest('.js-field-group')?.classList.contains('has-error')) {
            validatePhone();
         }
      });
   }

   if (messageInput) {
      messageInput.addEventListener('blur', validateMessage);
      messageInput.addEventListener('input', () => {
         if (messageInput.closest('.js-field-group')?.classList.contains('has-error')) {
            validateMessage();
         }
      });
   }

   if (privacyCheckbox) {
      privacyCheckbox.addEventListener('change', validatePrivacy);
   }

   // Form Submit Event
   form.addEventListener('submit', (e) => {
      e.preventDefault();

      // Check honeypot
      if (honeypot && honeypot.value !== '') {
         console.warn('Spam detected via honeypot');
         return;
      }

      // Validate all fields
      const isNameValid = validateName();
      const isEmailValid = validateEmail();
      const isPhoneValid = validatePhone();
      const isMessageValid = validateMessage();
      const isPrivacyValid = validatePrivacy();

      const isFormValid = isNameValid && isEmailValid && isPhoneValid && isMessageValid && isPrivacyValid;

      if (!isFormValid) {
         // Focus first invalid element
         const firstError = form.querySelector('.has-error input, .has-error textarea');
         if (firstError) {
            firstError.focus();
         }
         return;
      }

      // Start submission UI state
      form.classList.add('is-submitting');
      if (submitBtn) {
         submitBtn.disabled = true;
         const submitText = submitBtn.querySelector('.m-contact-form__submit-text');
         if (submitText) {
            submitText.setAttribute('data-original', submitText.textContent);
            submitText.textContent = messages.sending;
         }
      }

      if (statusBox) {
         statusBox.style.display = 'none';
         statusBox.className = 'm-contact-form__status js-contact-status';
         statusBox.textContent = '';
      }

      const formData = new FormData(form);

      fetch(form.action, {
         method: 'POST',
         body: formData,
         headers: {
            'X-Requested-With': 'XMLHttpRequest'
         }
      })
         .then((response) => response.text())
         .then((text) => {
            let isSuccess = false;
            let responseMsg = '';

            try {
               const json = JSON.parse(text);
               isSuccess = !!json.success;
               responseMsg = json.message || (isSuccess ? messages.sendSuccess : messages.sendError);
            } catch (err) {
               // Fallback: check for legacy delimiter ||||true
               if (text.includes('||||true')) {
                  isSuccess = true;
                  responseMsg = messages.sendSuccess;
               } else {
                  isSuccess = false;
                  responseMsg = messages.sendError;
               }
            }

            if (statusBox) {
               statusBox.style.display = 'block';
               if (isSuccess) {
                  statusBox.classList.add('is-success');
                  statusBox.textContent = responseMsg;
                  form.reset();

                  // Clear all error classes
                  form.querySelectorAll('.has-error').forEach((el) => el.classList.remove('has-error'));
               } else {
                  statusBox.classList.add('is-error');
                  statusBox.textContent = responseMsg || messages.sendError;
               }
            }
         })
         .catch((error) => {
            console.error('Contact submission error:', error);
            if (statusBox) {
               statusBox.style.display = 'block';
               statusBox.classList.add('is-error');
               statusBox.textContent = messages.sendError;
            }
         })
         .finally(() => {
            form.classList.remove('is-submitting');
            if (submitBtn) {
               submitBtn.disabled = false;
               const submitText = submitBtn.querySelector('.m-contact-form__submit-text');
               if (submitText) {
                  const original = submitText.getAttribute('data-original');
                  if (original) {
                     submitText.textContent = original;
                  }
               }
            }
         });
   });
};
